import { useContext, useMemo, useState } from "react";

import useDebounce from "@/Common/CustomHooks/useDebounce";
import { Guid } from "@/Common/Models/Guid";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { EmployeeContext } from "../Contexts/EmployeeContext";
import { FlowsheetSummary } from "../Models/FlowsheetSummary";
import { useFlowsheetSummaries } from "./useFlowsheetSummaries";
import { useGetFlowsheetName } from "./useGetFlowsheetName";
import { IQueryStatus } from "./useQuery";

/**
 * Custom hook to manage the flowsheet history for a patient.
 * @param param0 - An object containing the patientId.
 * @param param0.patientId - The ContactFacetId of the patient.
 * @returns An object containing the flowsheet history, loading state, search term, and refetch function.
 */
function useFlowsheetHistory({ patientId }: { patientId: Guid }): {
    flowsheetHistory: FlowsheetSummary[];
    isLoading: boolean;
    setSearchTerm: (term: string) => void;
    searchTerm: string;
    refetch: () => Promise<IQueryStatus<FlowsheetSummary[], any>>;
} {
    const { getName } = useGetFlowsheetName();

    const { data, isSuccess, refetch } = useFlowsheetSummaries({ patientId });

    const [searchTerm, setSearchTerm] = useState("");

    const searchTermDebounced = useDebounce(searchTerm, 300);

    const { getFullNameWithPrefixContactFacet } = useContext(EmployeeContext);

    const filteredData = useMemo(
        () => {
            if (!isSuccess)
                return [];

            return data.filter(flowsheet => {
                const searchItems = [
                    getName(flowsheet.Template),
                    getFullNameWithPrefixContactFacet(flowsheet.CreatedByEmployeeId),
                    LuxonDateTimeParser.parse(flowsheet.CreationDate).toFormat("yyyy-MM-dd")
                ];

                return searchItems.some(item => item.toLowerCase().includes((searchTermDebounced ?? "").toLowerCase()));
            })
                .sort((a, b) => b.CreationDate.getTime() - a.CreationDate.getTime());
        },
        [data, isSuccess, searchTermDebounced, getName, getFullNameWithPrefixContactFacet]
    );

    return {
        flowsheetHistory: filteredData,
        isLoading: !isSuccess,
        setSearchTerm,
        searchTerm,
        refetch
    };
}

export default useFlowsheetHistory;
